import type { OneTrustHelper, OneTrustGroupKey } from '~/plugins/one-trust-helper';

type HeadConfig = {
  hid: string;
  type: 'text/javascript';
  src: string;
  async: boolean;
};

const rotQueryParam = 'rot-ot';

const getOneTrustHelper = async (): Promise<OneTrustHelper> => {
  if (!window || !window.oneTrustHelper) return new Promise<never>(() => {});
  await window.oneTrustHelper.oneTrustReady;
  return window.oneTrustHelper;
};

const loaded = new Map<string, boolean>();

/**
 * Load the headerConfigs (e.g. like `useHead`) if the groupKeys are consented to.
 * It will also unload the scripts if consent is later revoked and reload the page.
 */
const otLoadHeader = async ({
  headConfigs,
  groupKeys,
}: {
  headConfigs: Array<HeadConfig>;
  groupKeys: OneTrustGroupKey[];
}): Promise<void> => {
  const oneTrustHelper = await getOneTrustHelper();
  const loadScripts = () => {
    if (!oneTrustHelper.isConsent(groupKeys)) {
      console.log(
        `OneTrust Consent not given for these group keys: ${groupKeys}, not loading scripts ${headConfigs.map((c) => c.hid).join(', ')}`
      );
      return;
    }

    headConfigs.forEach((config) => {
      if (loaded.get(config.hid)) return;
      const script = document.createElement('script');
      script.type = config.type;
      script.src = config.src;
      script.async = config.async;
      script.id = config.hid;
      document.head.appendChild(script);
      loaded.set(config.hid, true);
    });
  };

  const unloadScripts = () => {
    if (oneTrustHelper.isConsent(groupKeys)) {
      return;
    }

    headConfigs.forEach((config) => {
      const existingScript = document.getElementById(config.hid);
      if (existingScript) {
        console.log(`Unloading OneTrust script with hid: ${config.hid}`);
        document.head.removeChild(existingScript);
        loaded.delete(config.hid);
        // remove the script and reload the page to ensure clean state
        setTimeout(() => {
          const url = new URL(document.location.href);
          url.searchParams.set(rotQueryParam, '1');
          document.location = url.toString();
        }, 100);
      }
    });
  };

  oneTrustHelper.pushUpdateGroupKeysCallback(() => {
    loadScripts();
    unloadScripts();
  });
  loadScripts();
};

export const useOneTrust = () => {
  return {
    otLoadHeader,
  };
};
